import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plane, Plus, ArrowLeft, X, Calendar, Image as ImageIcon, ShieldCheck, ShieldAlert } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import {
  getDroneRuns,
  createDroneRun,
  updateDroneRun,
  getPaddocks,
  getFlightVerification,
  upsertFlightVerification,
  signOffFlightVerification,
  getDroneRunThumbnails,
} from '../../services/api'
import StatCard from '../../components/StatCard'

const STATUS_COLORS = {
  pending: 'bg-gray-100 text-gray-700',
  processing: 'bg-blue-100 text-blue-700',
  processed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
}

export default function DroneRuns({ ranchId }) {
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editRun, setEditRun] = useState(null)
  const [verifyRun, setVerifyRun] = useState(null)
  const [thumbsRun, setThumbsRun] = useState(null)

  const { data: runs, isLoading } = useQuery({
    queryKey: ['droneRuns', ranchId],
    queryFn: () => getDroneRuns(ranchId),
  })

  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  const totalRuns = runs?.length || 0
  const processedRuns = runs?.filter(r => r.processing_status === 'processed').length || 0
  const totalPolygons = runs?.reduce((sum, r) => sum + Number(r.polygon_count || 0), 0) || 0

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['droneRuns'] })
    queryClient.invalidateQueries({ queryKey: ['flightVerification'] })
    setShowCreate(false)
    setEditRun(null)
    setVerifyRun(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Drone Runs</h1>
            <p className="text-gray-500 mt-1">
              UAV flight campaigns — orthomosaics + rasters feeding the classifier label store
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" /> New Flight
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard title="Total Flights" value={totalRuns} icon={<Plane className="h-5 w-5" />} />
        <StatCard title="Processed" value={processedRuns} icon={<ImageIcon className="h-5 w-5" />} />
        <StatCard title="Labelled Polygons" value={totalPolygons} icon={<Calendar className="h-5 w-5" />} />
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : totalRuns === 0 ? (
          <div className="p-8 text-center text-gray-400">
            No drone flights recorded yet. Register a flight to begin building the classifier label store.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sensors</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Altitude</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GSD</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paddocks</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Polygons</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verification</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Validation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {runs.map(r => {
                const ageDays = Math.floor(
                  (Date.now() - new Date(r.flight_date).getTime()) / 86400000
                )
                // Traffic-light age indicator vs 24h verification window.
                const ageClass = r.is_verified
                  ? 'bg-green-100 text-green-700'
                  : ageDays <= 1
                    ? 'bg-blue-100 text-blue-700'
                    : ageDays <= 3
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-red-100 text-red-800'
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{format(parseISO(r.flight_date), 'dd MMM yyyy')}</td>
                    <td className="px-4 py-3 text-sm">{r.sensors || '-'}</td>
                    <td className="px-4 py-3 text-sm font-mono">{r.altitude_m ? `${r.altitude_m} m` : '-'}</td>
                    <td className="px-4 py-3 text-sm font-mono">{r.gsd_cm_per_pixel ? `${r.gsd_cm_per_pixel} cm/px` : '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      {Array.isArray(r.paddocks_covered) ? r.paddocks_covered.length : 0}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{r.polygon_count || 0}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[r.processing_status] || STATUS_COLORS.pending}`}>
                        {r.processing_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 ${ageClass}`}>
                        {r.is_verified
                          ? <><ShieldCheck className="h-3.5 w-3.5" /> verified</>
                          : <><ShieldAlert className="h-3.5 w-3.5" /> {ageDays}d old</>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {/* Tri-state Phase-2 gate verdict: pass / FAIL / not
                          assessed (null = no gating metric measurable yet). */}
                      {r.validation_passed === true && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">pass</span>
                      )}
                      {r.validation_passed === false && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">FAIL</span>
                      )}
                      {(r.validation_passed === null || r.validation_passed === undefined) && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
                              title="No gating metric assessable yet (check points / PIF baseline pending)">
                          not assessed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm space-x-3">
                      <button
                        onClick={() => setVerifyRun(r)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Verify
                      </button>
                      <button
                        onClick={() => setEditRun(r)}
                        className="text-green-600 hover:text-green-700 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setThumbsRun(r)}
                        className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                      >
                        Imagery
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <DroneRunModal
          ranchId={ranchId}
          paddocks={paddocks || []}
          onClose={() => setShowCreate(false)}
          onSuccess={invalidate}
        />
      )}
      {editRun && (
        <DroneRunModal
          ranchId={ranchId}
          paddocks={paddocks || []}
          run={editRun}
          onClose={() => setEditRun(null)}
          onSuccess={invalidate}
        />
      )}
      {verifyRun && (
        <VerificationModal
          run={verifyRun}
          onClose={() => setVerifyRun(null)}
          onSuccess={invalidate}
        />
      )}
      {thumbsRun && (
        <ThumbnailsModal run={thumbsRun} onClose={() => setThumbsRun(null)} />
      )}
    </div>
  )
}

function VerificationModal({ run, onClose, onSuccess }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState(null)
  const [form, setForm] = useState({
    cal_panel_photo_id: '',
    ortho_screenshot_id: '',
    ndvi_screenshot_id: '',
    drive_link: '',
    supervisor_notes: '',
  })

  const { data: existing } = useQuery({
    queryKey: ['flightVerification', run.id],
    queryFn: () => getFlightVerification(run.id),
  })

  // React Query v5 removed onSuccess on useQuery; sync form state once
  // the query resolves via an effect keyed on the row identity.
  useEffect(() => {
    if (!existing) return
    setForm({
      cal_panel_photo_id: existing.cal_panel_photo_id || '',
      ortho_screenshot_id: existing.ortho_screenshot_id || '',
      ndvi_screenshot_id: existing.ndvi_screenshot_id || '',
      drive_link: existing.drive_link || '',
      supervisor_notes: existing.supervisor_notes || '',
    })
  }, [existing])

  const upsert = useMutation({
    mutationFn: (payload) => upsertFlightVerification(run.id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['flightVerification', run.id] }),
    onError: (err) => setError(err.response?.data?.detail || 'Failed to save'),
  })

  const sign = useMutation({
    mutationFn: () => signOffFlightVerification(run.id, { supervisor_notes: form.supervisor_notes || null }),
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Sign-off failed'),
  })

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const handleSave = (e) => {
    e.preventDefault()
    setError(null)
    upsert.mutate({
      cal_panel_photo_id: form.cal_panel_photo_id || null,
      ortho_screenshot_id: form.ortho_screenshot_id || null,
      ndvi_screenshot_id: form.ndvi_screenshot_id || null,
      drive_link: form.drive_link || null,
      supervisor_notes: form.supervisor_notes || null,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            Flight Verification — {format(parseISO(run.flight_date), 'dd MMM yyyy')}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-4 space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}

          <p className="text-xs text-gray-500">
            4-item post-flight checklist (drone-operator §6 / mig 095). All items required for supervisor sign-off.
          </p>

          <div>
            <label className="label">Cal Panel Photo (media id)</label>
            <input className="input w-full font-mono text-xs" value={form.cal_panel_photo_id}
              onChange={e => update('cal_panel_photo_id', e.target.value)}
              placeholder="UUID from media.photos" />
          </div>
          <div>
            <label className="label">Orthomosaic Screenshot (media id)</label>
            <input className="input w-full font-mono text-xs" value={form.ortho_screenshot_id}
              onChange={e => update('ortho_screenshot_id', e.target.value)}
              placeholder="UUID" />
          </div>
          <div>
            <label className="label">NDVI Screenshot (media id)</label>
            <input className="input w-full font-mono text-xs" value={form.ndvi_screenshot_id}
              onChange={e => update('ndvi_screenshot_id', e.target.value)}
              placeholder="UUID" />
          </div>
          <div>
            <label className="label">Raw Imagery Drive Link</label>
            <input type="url" className="input w-full" value={form.drive_link}
              onChange={e => update('drive_link', e.target.value)}
              placeholder="https://drive.google.com/..." />
          </div>
          <div>
            <label className="label">Supervisor Notes</label>
            <textarea className="input w-full" rows={2} value={form.supervisor_notes}
              onChange={e => update('supervisor_notes', e.target.value)} />
          </div>

          {existing?.is_supervisor_verified && (
            <div className="flex items-center gap-2 p-3 bg-green-50 text-green-800 rounded text-sm">
              <ShieldCheck className="h-4 w-4" />
              Supervisor verified at {existing.supervisor_verified_at}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <button type="button" onClick={() => sign.mutate()}
              disabled={sign.isPending}
              className="btn bg-blue-600 text-white hover:bg-blue-700">
              {sign.isPending ? 'Signing…' : 'Supervisor Sign-Off'}
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={onClose}
                className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">
                Close
              </button>
              <button type="submit" disabled={upsert.isPending}
                className="btn bg-green-600 text-white hover:bg-green-700">
                {upsert.isPending ? 'Saving…' : 'Save Artifacts'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

function DroneRunModal({ ranchId, paddocks, run, onClose, onSuccess }) {
  const isEdit = Boolean(run)
  const [form, setForm] = useState(() => ({
    flight_date: run?.flight_date || new Date().toISOString().split('T')[0],
    altitude_m: run?.altitude_m || '',
    sensors: run?.sensors || 'rgb,multispectral',
    pilot: run?.pilot || '',
    weather_conditions: run?.weather_conditions || '',
    rgb_ortho_url: run?.rgb_ortho_url || '',
    ms_ortho_url: run?.ms_ortho_url || '',
    ndvi_raster_url: run?.ndvi_raster_url || '',
    ndre_raster_url: run?.ndre_raster_url || '',
    gsd_cm_per_pixel: run?.gsd_cm_per_pixel || '',
    overlap_pct: run?.overlap_pct || '',
    processing_status: run?.processing_status || 'pending',
    notes: run?.notes || '',
    paddock_ids: Array.isArray(run?.paddocks_covered)
      ? run.paddocks_covered.map(p => p.id)
      : [],
  }))
  const [error, setError] = useState(null)

  const mutation = useMutation({
    mutationFn: (payload) => isEdit ? updateDroneRun(run.id, payload) : createDroneRun(payload),
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to save drone run'),
  })

  const update = (field, val) => setForm(prev => ({ ...prev, [field]: val }))

  const togglePaddock = (paddockId) => {
    setForm(prev => ({
      ...prev,
      paddock_ids: prev.paddock_ids.includes(paddockId)
        ? prev.paddock_ids.filter(id => id !== paddockId)
        : [...prev.paddock_ids, paddockId],
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const paddocks_covered = form.paddock_ids
      .map(id => paddocks.find(p => p.id === id))
      .filter(Boolean)
      .map(p => ({ id: p.id, code: p.code, name: p.name }))

    const payload = {
      ...(isEdit ? {} : { ranch_id: ranchId }),
      flight_date: form.flight_date,
      paddocks_covered: paddocks_covered.length > 0 ? paddocks_covered : null,
      altitude_m: form.altitude_m ? parseFloat(form.altitude_m) : null,
      sensors: form.sensors || null,
      pilot: form.pilot || null,
      weather_conditions: form.weather_conditions || null,
      rgb_ortho_url: form.rgb_ortho_url || null,
      ms_ortho_url: form.ms_ortho_url || null,
      ndvi_raster_url: form.ndvi_raster_url || null,
      ndre_raster_url: form.ndre_raster_url || null,
      gsd_cm_per_pixel: form.gsd_cm_per_pixel ? parseFloat(form.gsd_cm_per_pixel) : null,
      overlap_pct: form.overlap_pct ? parseFloat(form.overlap_pct) : null,
      notes: form.notes || null,
      ...(isEdit ? { processing_status: form.processing_status } : {}),
    }
    mutation.mutate(payload)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Plane className="h-5 w-5 text-green-600" />
            {isEdit ? 'Edit Drone Run' : 'Register Drone Flight'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Flight Date *</label>
              <input type="date" className="input" value={form.flight_date}
                onChange={e => update('flight_date', e.target.value)} required />
            </div>
            <div>
              <label className="label">Sensors</label>
              <input type="text" className="input" placeholder="rgb,multispectral"
                value={form.sensors} onChange={e => update('sensors', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Altitude (m AGL)</label>
              <input type="number" className="input" value={form.altitude_m}
                onChange={e => update('altitude_m', e.target.value)} />
            </div>
            <div>
              <label className="label">GSD (cm/px)</label>
              <input type="number" step="0.01" className="input" value={form.gsd_cm_per_pixel}
                onChange={e => update('gsd_cm_per_pixel', e.target.value)} />
            </div>
            <div>
              <label className="label">Overlap %</label>
              <input type="number" className="input" value={form.overlap_pct}
                onChange={e => update('overlap_pct', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Pilot</label>
              <input type="text" className="input" value={form.pilot}
                onChange={e => update('pilot', e.target.value)} />
            </div>
            <div>
              <label className="label">Weather</label>
              <input type="text" className="input" placeholder="clear, light wind"
                value={form.weather_conditions}
                onChange={e => update('weather_conditions', e.target.value)} />
            </div>
          </div>

          <div>
            <label className="label">Paddocks Covered</label>
            <div className="max-h-32 overflow-y-auto border border-gray-200 rounded p-2 flex flex-wrap gap-2">
              {paddocks.map(p => (
                <label key={p.id} className={`cursor-pointer px-2 py-1 rounded text-xs border ${form.paddock_ids.includes(p.id) ? 'bg-green-50 border-green-400 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                  <input type="checkbox" className="hidden"
                    checked={form.paddock_ids.includes(p.id)}
                    onChange={() => togglePaddock(p.id)} />
                  {p.name}
                </label>
              ))}
            </div>
          </div>

          <div className="border-t pt-4 space-y-2">
            <div className="text-xs uppercase text-gray-500 font-medium">Processed Asset URLs</div>
            <input type="url" className="input" placeholder="RGB orthomosaic URL"
              value={form.rgb_ortho_url} onChange={e => update('rgb_ortho_url', e.target.value)} />
            <input type="url" className="input" placeholder="Multispectral orthomosaic URL"
              value={form.ms_ortho_url} onChange={e => update('ms_ortho_url', e.target.value)} />
            <input type="url" className="input" placeholder="NDVI raster URL"
              value={form.ndvi_raster_url} onChange={e => update('ndvi_raster_url', e.target.value)} />
            <input type="url" className="input" placeholder="NDRE raster URL"
              value={form.ndre_raster_url} onChange={e => update('ndre_raster_url', e.target.value)} />
          </div>

          {isEdit && (
            <div>
              <label className="label">Processing Status</label>
              <select className="input" value={form.processing_status}
                onChange={e => update('processing_status', e.target.value)}>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="processed">Processed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          )}

          <div>
            <label className="label">Notes</label>
            <textarea className="input" rows={2} value={form.notes}
              onChange={e => update('notes', e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">Cancel</button>
            <button type="submit" disabled={mutation.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700">
              {mutation.isPending ? 'Saving...' : (isEdit ? 'Update' : 'Register')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ThumbnailsModal({ run, onClose }) {
  // Phase 4: fixed-scale NDVI/CHM thumbnails rendered at ingest. Signed URLs
  // expire after 6h — refetch on open, never cache across sessions.
  const { data: thumbs, isLoading, error } = useQuery({
    queryKey: ['droneRunThumbs', run.id],
    queryFn: () => getDroneRunThumbnails(run.id),
    staleTime: 0,
  })
  const ranchThumbs = (thumbs || []).filter(t => t.scope === 'ranch')
  const paddockThumbs = (thumbs || []).filter(t => t.scope !== 'ranch')
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            Flight imagery — {format(parseISO(run.flight_date), 'dd MMM yyyy')}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        {isLoading && <div className="p-8 text-center text-gray-400">Loading imagery…</div>}
        {error && (
          <div className="p-8 text-center text-red-500">Could not load imagery.</div>
        )}
        {thumbs && thumbs.length === 0 && (
          <div className="p-8 text-center text-gray-400">
            No thumbnails for this flight yet — they are rendered at ingest for
            flights processed after the imagery update (older flights can be
            reprocessed).
          </div>
        )}
        {ranchThumbs.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Ranch overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ranchThumbs.map(t => (
                <figure key={`${t.scope}-${t.kind}`}>
                  <img src={t.url} alt={`Ranch ${t.kind.toUpperCase()}`}
                       className="w-full rounded-lg border bg-gray-50" loading="lazy" />
                  <figcaption className="text-xs text-gray-500 mt-1 uppercase">{t.kind}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}
        {paddockThumbs.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Per paddock</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {paddockThumbs.map(t => (
                <figure key={`${t.scope}-${t.kind}`}>
                  <img src={t.url} alt={`${t.scope} ${t.kind.toUpperCase()}`}
                       className="w-full rounded border bg-gray-50" loading="lazy" />
                  <figcaption className="text-xs text-gray-500 mt-0.5">
                    {t.scope} · {t.kind.toUpperCase()}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
